import { useEffect, useMemo, useState } from "react";
import { FileText, Plus, Trash2, Download, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, money, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PDF_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/proformas`;
const blankRow = () => ({ name: "", unit: "", quantity: 1, unit_price: 0, gst_rate: 18 });

export const ProformaDialog = ({ prospect }) => {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [rows, setRows] = useState([blankRow()]);
  const [bill, setBill] = useState({ name: "", address: "", gstin: "", phone: "" });
  const [notes, setNotes] = useState("");
  const [installCost, setInstallCost] = useState("");
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [justCreated, setJustCreated] = useState(null);

  const waHref = (p) => {
    const raw = (p.bill_to_phone || prospect?.mobile || "").replace(/\D/g, "");
    const num = raw.length === 10 ? `91${raw}` : raw;
    const pdf = `${PDF_BASE}/${p.id}/report.pdf`;
    const msg = `Hi ${p.bill_to_name || prospect?.business_name || "there"}, please find your Veloskin proforma invoice ${p.number} for ${money(p.grand_total)}.\nView / download: ${pdf}`;
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  };

  const markSent = async (p) => {
    if (!p?.id) return;
    try {
      await api.post(`/proformas/${p.id}/mark-sent`);
      const now = new Date().toISOString();
      setList((l) => l.map((x) => (x.id === p.id ? { ...x, last_sent_at: now } : x)));
      setJustCreated((j) => (j && j.id === p.id ? { ...j, last_sent_at: now } : j));
      toast.success("Logged as sent on WhatsApp");
    } catch (e) {
      /* non-blocking */
    }
  };

  useEffect(() => {
    if (!open) return;
    api.get("/catalog").then(({ data }) => setCatalog(data || [])).catch(() => {});
    setBill({
      name: prospect?.business_name || "",
      address: prospect?.address || [prospect?.city, prospect?.region].filter(Boolean).join(", "),
      gstin: prospect?.gstin || "",
      phone: prospect?.mobile || "",
    });
    setRows([blankRow()]);
    setNotes("");
    setJustCreated(null);
    if (prospect?.id)
      api.get(`/proformas?prospect_id=${prospect.id}`).then(({ data }) => setList(data || [])).catch(() => {});
  }, [open, prospect]);

  const totals = useMemo(() => {
    let sub = 0;
    let tax = 0;
    rows.forEach((r) => {
      const amt = (Number(r.quantity) || 0) * (Number(r.unit_price) || 0);
      sub += amt;
      tax += (amt * (Number(r.gst_rate) || 0)) / 100;
    });
    return { sub, tax, half: tax / 2, total: sub + tax + (Number(installCost) || 0) };
  }, [rows, installCost]);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, x) => (x === i ? { ...r, ...patch } : r)));
  const pickSku = (i, id) => {
    const s = catalog.find((c) => c.id === id);
    if (s)
      setRow(i, {
        name: s.name,
        unit: s.unit || "",
        unit_price: s.hq_sale_price ?? 0,
        gst_rate: s.gst_rate ?? 18,
      });
  };

  const save = async () => {
    const items = rows.filter((r) => r.name.trim() && Number(r.quantity) > 0);
    if (!bill.name.trim()) return toast.error("Enter who the invoice is billed to");
    if (!items.length) return toast.error("Add at least one product");
    setBusy(true);
    try {
      const { data } = await api.post("/proformas", {
        prospect_id: prospect?.id || null,
        bill_to_name: bill.name,
        bill_to_address: bill.address,
        bill_to_gstin: bill.gstin,
        bill_to_phone: bill.phone,
        items: items.map((r) => ({
          name: r.name,
          unit: r.unit,
          quantity: Number(r.quantity),
          unit_price: Number(r.unit_price),
          gst_rate: Number(r.gst_rate),
        })),
        notes,
        installation_cost: Number(installCost) || 0,
      });
      toast.success(`Proforma ${data.number} created`);
      window.open(`${PDF_BASE}/${data.id}/report.pdf`, "_blank");
      setJustCreated(data);
      setRows([blankRow()]);
      setNotes("");
      setInstallCost("");
      if (prospect?.id) setList((l) => [data, ...l]);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="min-h-[44px]" data-testid="create-proforma-btn">
          <FileText className="mr-2 h-4 w-4" /> Proforma invoice
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="proforma-dialog">
        <DialogHeader>
          <DialogTitle>Create proforma invoice</DialogTitle>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Billed to *</Label>
            <Input
              className="mt-1.5"
              data-testid="proforma-billto-name"
              value={bill.name}
              onChange={(e) => setBill((b) => ({ ...b, name: e.target.value }))}
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input
              className="mt-1.5"
              data-testid="proforma-billto-phone"
              value={bill.phone}
              onChange={(e) => setBill((b) => ({ ...b, phone: e.target.value }))}
            />
          </div>
          <div>
            <Label>GSTIN</Label>
            <Input
              className="mt-1.5"
              data-testid="proforma-billto-gstin"
              value={bill.gstin}
              onChange={(e) => setBill((b) => ({ ...b, gstin: e.target.value }))}
            />
          </div>
          <div>
            <Label>Address</Label>
            <Input
              className="mt-1.5"
              data-testid="proforma-billto-address"
              value={bill.address}
              onChange={(e) => setBill((b) => ({ ...b, address: e.target.value }))}
            />
          </div>
        </div>

        <div className="mt-2 space-y-2" data-testid="proforma-items">
          <Label>Products</Label>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 items-end gap-2" data-testid={`proforma-row-${i}`}>
              <div className="col-span-12 sm:col-span-4">
                <Select onValueChange={(v) => pickSku(i, v)}>
                  <SelectTrigger className="min-h-[40px]" data-testid={`proforma-sku-${i}`}>
                    <SelectValue placeholder="Pick SKU / type below" />
                  </SelectTrigger>
                  <SelectContent>
                    {catalog.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  className="mt-1 min-h-[36px]"
                  placeholder="Product name"
                  data-testid={`proforma-name-${i}`}
                  value={r.name}
                  onChange={(e) => setRow(i, { name: e.target.value })}
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <Label className="text-[10px] text-slate-500">Qty</Label>
                <Input
                  inputMode="decimal"
                  className="min-h-[40px]"
                  data-testid={`proforma-qty-${i}`}
                  value={r.quantity}
                  onChange={(e) => setRow(i, { quantity: e.target.value })}
                />
              </div>
              <div className="col-span-4 sm:col-span-3">
                <Label className="text-[10px] text-slate-500">Rate</Label>
                <Input
                  inputMode="decimal"
                  className="min-h-[40px]"
                  data-testid={`proforma-rate-${i}`}
                  value={r.unit_price}
                  onChange={(e) => setRow(i, { unit_price: e.target.value })}
                />
              </div>
              <div className="col-span-3 sm:col-span-2">
                <Label className="text-[10px] text-slate-500">GST %</Label>
                <Input
                  inputMode="decimal"
                  className="min-h-[40px]"
                  data-testid={`proforma-gst-${i}`}
                  value={r.gst_rate}
                  onChange={(e) => setRow(i, { gst_rate: e.target.value })}
                />
              </div>
              <div className="col-span-1 flex justify-end">
                {rows.length > 1 && (
                  <button
                    type="button"
                    data-testid={`proforma-remove-${i}`}
                    onClick={() => setRows((rs) => rs.filter((_, x) => x !== i))}
                    className="p-2 text-slate-400 hover:text-crimson-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            data-testid="proforma-add-row"
            onClick={() => setRows((rs) => [...rs, blankRow()])}
          >
            <Plus className="mr-1 h-4 w-4" /> Add product
          </Button>
        </div>

        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Notes / terms</Label>
            <Textarea
              className="mt-1.5"
              rows={3}
              data-testid="proforma-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <div className="rounded-md border border-brand-line bg-slate-50 p-3 text-sm" data-testid="proforma-totals">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{money(totals.sub)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">CGST</span><span>{money(totals.half)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">SGST</span><span>{money(totals.half)}</span></div>
            <div className="mt-2 flex items-center justify-between gap-2 border-t border-brand-line pt-2">
              <Label className="text-slate-500" htmlFor="proforma-install">Installation Cost</Label>
              <Input
                id="proforma-install"
                type="number"
                min="0"
                className="h-8 w-32 text-right"
                data-testid="proforma-installation-cost"
                placeholder="0"
                value={installCost}
                onChange={(e) => setInstallCost(e.target.value)}
              />
            </div>
            <div className="mt-1 flex justify-between border-t border-brand-line pt-1 font-semibold">
              <span>Grand total</span><span data-testid="proforma-grand-total">{money(totals.total)}</span>
            </div>
          </div>
        </div>

        {list.length > 0 && (
          <div className="mt-2" data-testid="proforma-history">
            <Label className="text-xs text-slate-500">Earlier proformas</Label>
            <div className="mt-1 space-y-1">
              {list.map((p) => (
                <div key={p.id} className="flex items-center justify-between rounded border border-brand-line px-2 py-1 text-xs">
                  <span>
                    {p.number} · {money(p.grand_total)} · {fmtDate(p.created_at)}
                    {p.last_sent_at && (
                      <span className="ml-1 font-semibold text-emerald-600" data-testid={`proforma-sent-${p.id}`}>
                        · Sent {fmtDate(p.last_sent_at)}
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-3">
                  <a
                    href={`${PDF_BASE}/${p.id}/report.pdf`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-brand hover:underline"
                    data-testid={`proforma-pdf-${p.id}`}
                  >
                    <Download className="h-3 w-3" /> PDF
                  </a>
                  <a
                    href={waHref(p)}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => markSent(p)}
                    className="inline-flex items-center gap-1 font-semibold text-emerald-600 hover:underline"
                    data-testid={`proforma-wa-${p.id}`}
                  >
                    <MessageCircle className="h-3 w-3" /> WhatsApp
                  </a>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {justCreated && (
            <a
              href={waHref(justCreated)}
              target="_blank"
              rel="noreferrer"
              onClick={() => markSent(justCreated)}
              data-testid="proforma-send-whatsapp"
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-md bg-emerald-600 px-4 text-sm font-semibold text-white hover:bg-emerald-700"
            >
              <MessageCircle className="h-4 w-4" />
              {justCreated.last_sent_at ? `Re-send ${justCreated.number} on WhatsApp` : `Send ${justCreated.number} on WhatsApp`}
            </a>
          )}
          <Button data-testid="proforma-save-btn" disabled={busy} onClick={save}>
            {busy ? "Creating…" : "Create & download PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
